import React from "react";
import { Button, CircularProgress, alpha } from "@mui/material";
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";

const VARIANTS = {
  save: { icon: <SaveIcon sx={{ fontSize: 15 }} />, label: "Kaydet" },
  delete: { icon: <DeleteIcon sx={{ fontSize: 15 }} />, label: "Sil" },
  add: { icon: <AddIcon sx={{ fontSize: 15 }} />, label: "Ekle" },
  send: { icon: <SendIcon sx={{ fontSize: 15 }} />, label: "Gönder" },
  confirm: { icon: <CheckIcon sx={{ fontSize: 15 }} />, label: "Onayla" },
  edit: { icon: <EditIcon sx={{ fontSize: 15 }} />, label: "Düzenle" },
};

/**
 * Standart aksiyon butonu.
 * @param {"save"|"delete"|"add"|"send"|"confirm"|"edit"} variant
 * @param {object} palette - Klinik tema paleti (opsiyonel)
 * @param {boolean} loading
 * @param {string} [label] - Özel label (variant default'unu override eder;
 *   children da kabul edilir — label öncelikli)
 * @param {"small"|"medium"} [size="small"]
 * @param {boolean} [compact=false] - Header içi mini buton
 * @param {"solid"|"soft"|"outline"} [tone="solid"] - "soft": açık zemin + renkli
 *   metin/kenar (liste satırı içi ikincil aksiyonlar, ör. "İptal Et");
 *   "outline": beyaz zemin + gri kenar (geri / detaya git gibi ikincil gezinme)
 * @param {ReactNode} [icon] - Varsayılan ikonun yerine; yüklenirken yine spinner gösterilir
 */
export default function ActionButton({
  variant = "save",
  palette,
  loading = false,
  label,
  size = "small",
  compact = false,
  tone = "solid",
  icon,
  disabled,
  sx = {},
  children,
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.save;
  const isDelete = variant === "delete";
  const isSoft = tone === "soft";
  const isOutline = tone === "outline";

  const accent = isDelete ? COLORS.error : (palette?.primary || COLORS.slate700);
  const bgColor = isOutline ? COLORS.white : isSoft ? alpha(accent, 0.08) : accent;
  const bgHover = isOutline
    ? COLORS.slate50
    : isSoft
      ? alpha(accent, 0.14)
      : isDelete ? COLORS.errorDark : (palette?.primaryDark || COLORS.slate800);
  const textColor = isOutline
    ? (isDelete ? COLORS.errorDark : COLORS.slate700)
    : isSoft
      ? (isDelete ? COLORS.errorDark : accent)
      : isDelete ? COLORS.white : (palette?.contrastText || COLORS.white);

  return (
    <Button
      size={size}
      variant="contained"
      startIcon={loading ? <CircularProgress size={compact ? 12 : 14} color="inherit" /> : (icon || v.icon)}
      disabled={loading || disabled}
      sx={{
        bgcolor: bgColor,
        color: textColor,
        ...(isSoft ? { border: `1px solid ${alpha(accent, 0.25)}` } : {}),
        ...(isOutline ? { border: `1px solid ${COLORS.slate300}` } : {}),
        "&:hover": { bgcolor: bgHover, boxShadow: "none" },
        borderRadius: compact ? 1.5 : 2,
        fontWeight: 700,
        textTransform: "none",
        fontSize: compact ? "0.75rem" : "0.8125rem",
        px: compact ? 2 : 3,
        py: compact ? 0.5 : 1,
        boxShadow: "none",
        minHeight: compact ? 30 : 36,
        "&:disabled": { opacity: 0.6 },
        ...sx,
      }}
      {...rest}
    >
      {label || children || v.label}
    </Button>
  );
}
